import React, { useState, useEffect } from 'react';
import { 
  Calculator, Settings, ChevronDown, Save, Share2, 
  BarChart3, Check, Info, AlertCircle
} from 'lucide-react';
import { Routes, Route, Outlet, useNavigate } from 'react-router-dom';

// Default LSAT scale - approximated conversion from raw to scaled score
const defaultLSATScale = {
  // Format: [raw score, scaled score]
  '101': 180, '100': 180, '99': 179, '98': 178, '97': 177, '96': 176,
  '95': 175, '94': 174, '93': 173, '92': 172, '91': 171, '90': 170,
  '89': 169, '88': 168, '87': 167, '86': 166, '85': 165, '84': 164,
  '83': 163, '82': 162, '81': 161, '80': 160, '79': 159, '78': 158,
  '77': 157, '76': 156, '75': 155, '74': 154, '73': 153, '72': 152,
  '71': 151, '70': 150, '69': 149, '68': 148, '67': 147, '66': 146,
  '65': 145, '64': 144, '63': 143, '62': 142, '61': 141, '60': 140,
  '59': 139, '58': 138, '57': 137, '56': 136, '55': 135, '54': 134,
  '53': 133, '52': 132, '51': 131, '50': 130, '49': 129, '48': 128,
  '47': 127, '46': 126, '45': 125, '44': 124, '43': 123, '42': 122,
  '41': 121, '40': 120, '39': 120, '38': 120, '37': 120, '36': 120,
  '35': 120, '34': 120, '33': 120, '32': 120, '31': 120, '30': 120,
  '29': 120, '28': 120, '27': 120, '26': 120, '25': 120, '24': 120,
  '23': 120, '22': 120, '21': 120, '20': 120, '19': 120, '18': 120,
  '17': 120, '16': 120, '15': 120, '14': 120, '13': 120, '12': 120,
  '11': 120, '10': 120, '9': 120, '8': 120, '7': 120, '6': 120,
  '5': 120, '4': 120, '3': 120, '2': 120, '1': 120, '0': 120
};

// Slightly more lenient scale for June 2023
const june2023Scale = {
  '101': 180, '100': 180, '99': 180, '98': 179, '97': 178, '96': 177,
  '95': 176, '94': 175, '93': 174, '92': 173, '91': 172, '90': 171,
  '89': 170, '88': 169, '87': 168, '86': 167, '85': 166, '84': 165,
  '83': 164, '82': 163, '81': 162, '80': 161, '79': 160, '78': 159,
  '77': 158, '76': 157, '75': 156, '74': 155, '73': 154, '72': 153,
  '71': 152, '70': 151, '69': 150, '68': 149, '67': 148, '66': 147,
  '65': 146, '64': 145, '63': 144, '62': 143, '61': 142, '60': 141,
  '59': 140, '58': 139, '57': 138, '56': 137, '55': 136, '54': 135,
  '53': 134, '52': 133, '51': 132, '50': 131, '49': 130, '48': 129,
  '47': 128, '46': 127, '45': 126, '44': 125, '43': 124, '42': 123,
  '41': 122, '40': 121, '39': 120, '38': 120, '37': 120, '36': 120,
  '35': 120, '34': 120, '33': 120, '32': 120, '31': 120, '30': 120,
  '29': 120, '28': 120, '27': 120, '26': 120, '25': 120, '24': 120,
  '23': 120, '22': 120, '21': 120, '20': 120, '19': 120, '18': 120,
  '17': 120, '16': 120, '15': 120, '14': 120, '13': 120, '12': 120,
  '11': 120, '10': 120, '9': 120, '8': 120, '7': 120, '6': 120,
  '5': 120, '4': 120, '3': 120, '2': 120, '1': 120, '0': 120
};

// Stricter scale for October 2023
const october2023Scale = {
  '101': 180, '100': 179, '99': 178, '98': 177, '97': 176, '96': 175,
  '95': 174, '94': 173, '93': 172, '92': 171, '91': 170, '90': 169,
  '89': 168, '88': 167, '87': 166, '86': 165, '85': 164, '84': 163,
  '83': 162, '82': 161, '81': 160, '80': 159, '79': 158, '78': 157,
  '77': 156, '76': 155, '75': 154, '74': 153, '73': 152, '72': 151,
  '71': 150, '70': 149, '69': 148, '68': 147, '67': 146, '66': 145,
  '65': 144, '64': 143, '63': 142, '62': 141, '61': 140, '60': 139,
  '59': 138, '58': 137, '57': 136, '56': 135, '55': 134, '54': 133,
  '53': 132, '52': 131, '51': 130, '50': 129, '49': 128, '48': 127,
  '47': 126, '46': 125, '45': 124, '44': 123, '43': 122, '42': 121,
  '41': 120, '40': 120, '39': 120, '38': 120, '37': 120, '36': 120,
  '35': 120, '34': 120, '33': 120, '32': 120, '31': 120, '30': 120,
  '29': 120, '28': 120, '27': 120, '26': 120, '25': 120, '24': 120,
  '23': 120, '22': 120, '21': 120, '20': 120, '19': 120, '18': 120,
  '17': 120, '16': 120, '15': 120, '14': 120, '13': 120, '12': 120,
  '11': 120, '10': 120, '9': 120, '8': 120, '7': 120, '6': 120,
  '5': 120, '4': 120, '3': 120, '2': 120, '1': 120, '0': 120
};

// More lenient scale for January 2024
const january2024Scale = {
  '101': 180, '100': 180, '99': 180, '98': 180, '97': 179, '96': 178,
  '95': 177, '94': 176, '93': 175, '92': 174, '91': 173, '90': 172,
  '89': 171, '88': 170, '87': 169, '86': 168, '85': 167, '84': 166,
  '83': 165, '82': 164, '81': 163, '80': 162, '79': 161, '78': 160,
  '77': 159, '76': 158, '75': 157, '74': 156, '73': 155, '72': 154,
  '71': 153, '70': 152, '69': 151, '68': 150, '67': 149, '66': 148,
  '65': 147, '64': 146, '63': 145, '62': 144, '61': 143, '60': 142,
  '59': 141, '58': 140, '57': 139, '56': 138, '55': 137, '54': 136,
  '53': 135, '52': 134, '51': 133, '50': 132, '49': 131, '48': 130,
  '47': 129, '46': 128, '45': 127, '44': 126, '43': 125, '42': 124,
  '41': 123, '40': 122, '39': 121, '38': 120, '37': 120, '36': 120,
  '35': 120, '34': 120, '33': 120, '32': 120, '31': 120, '30': 120,
  '29': 120, '28': 120, '27': 120, '26': 120, '25': 120, '24': 120,
  '23': 120, '22': 120, '21': 120, '20': 120, '19': 120, '18': 120,
  '17': 120, '16': 120, '15': 120, '14': 120, '13': 120, '12': 120,
  '11': 120, '10': 120, '9': 120, '8': 120, '7': 120, '6': 120,
  '5': 120, '4': 120, '3': 120, '2': 120, '1': 120, '0': 120
};

// Average scale for April 2024
const april2024Scale = {
  '101': 180, '100': 180, '99': 179, '98': 178, '97': 177, '96': 176,
  '95': 175, '94': 174, '93': 173, '92': 172, '91': 170, '90': 169,
  '89': 168, '88': 167, '87': 166, '86': 165, '85': 163, '84': 162,
  '83': 161, '82': 160, '81': 159, '80': 158, '79': 157, '78': 156,
  '77': 155, '76': 154, '75': 153, '74': 152, '73': 151, '72': 150,
  '71': 149, '70': 148, '69': 147, '68': 146, '67': 145, '66': 144,
  '65': 143, '64': 142, '63': 141, '62': 140, '61': 139, '60': 138,
  '59': 137, '58': 136, '57': 135, '56': 134, '55': 133, '54': 132,
  '53': 131, '52': 130, '51': 129, '50': 128, '49': 127, '48': 126,
  '47': 125, '46': 124, '45': 123, '44': 122, '43': 121, '42': 120,
  '41': 120, '40': 120, '39': 120, '38': 120, '37': 120, '36': 120,
  '35': 120, '34': 120, '33': 120, '32': 120, '31': 120, '30': 120,
  '29': 120, '28': 120, '27': 120, '26': 120, '25': 120, '24': 120,
  '23': 120, '22': 120, '21': 120, '20': 120, '19': 120, '18': 120,
  '17': 120, '16': 120, '15': 120, '14': 120, '13': 120, '12': 120,
  '11': 120, '10': 120, '9': 120, '8': 120, '7': 120, '6': 120,
  '5': 120, '4': 120, '3': 120, '2': 120, '1': 120, '0': 120
};

// Map of curve IDs to actual scale objects
const curveScales = {
  'default': defaultLSATScale,
  'june2023': june2023Scale,
  'october2023': october2023Scale,
  'january2024': january2024Scale,
  'april2024': april2024Scale
};

// LSAT Percentiles (approximated)
const percentiles = {
  '180': 99.9, '179': 99.9, '178': 99.9, '177': 99.8, '176': 99.7,
  '175': 99.6, '174': 99.5, '173': 99.4, '172': 99.2, '171': 99.0,
  '170': 97.4, '169': 97.0, '168': 96.0, '167': 94.5, '166': 93.0,
  '165': 91.0, '164': 89.3, '163': 87.1, '162': 84.5, '161': 81.9,
  '160': 80.0, '159': 77.0, '158': 74.0, '157': 70.6, '156': 67.3,
  '155': 64.0, '154': 60.5, '153': 57.0, '152': 53.5, '151': 50.0,
  '150': 44.0, '149': 40.0, '148': 36.0, '147': 33.0, '146': 29.5,
  '145': 26.0, '144': 23.0, '143': 20.0, '142': 17.5, '141': 15.0,
  '140': 13.0, '139': 11.0, '138': 9.5, '137': 8.0, '136': 6.7,
  '135': 5.5, '134': 4.5, '133': 3.7, '132': 3.0, '131': 2.4,
  '130': 2.0, '129': 1.5, '128': 1.2, '127': 1.0, '126': 0.9,
  '125': 0.8, '124': 0.6, '123': 0.5, '122': 0.4, '121': 0.3,
  '120': 0.1
};

// Predefined LSAT curves for different test administrations
const predefinedCurves = [
  { id: 'default', name: 'Default Scale' },
  { id: 'june2023', name: 'June 2023' },
  { id: 'october2023', name: 'October 2023' },
  { id: 'january2024', name: 'January 2024' },
  { id: 'april2024', name: 'April 2024' }
];

interface SectionConfig {
  id: string;
  name: string;
  totalQuestions: number;
  correctAnswers: number;
}

const LSATScoreConverter: React.FC = () => {
  return (
    <Routes>
      <Route path="" element={<ConverterLayout />}>
        <Route index element={<ConverterMainPage />} />
        <Route path="history" element={<ScoreHistoryPage />} />
        <Route path="settings" element={<ConverterSettingsPage />} />
      </Route>
    </Routes>
  );
};

// Shared layout component
export const ConverterLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-50">
      {/* Header section */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">LSAT Score Converter</h1>
              <p className="text-xl text-indigo-100 mb-6">
                Convert your raw scores to scaled scores (120-180), track your progress, and see how you compare to others.
              </p>
              <div className="flex space-x-4">
                {/* <button 
                  className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors shadow-md flex items-center"
                  onClick={() => {
                    // Reset to default sections with zeroed answers
                    const resetSections = sections.map(s => ({...s, correctAnswers: 0}));
                    setSections(resetSections);
                    localStorage.removeItem('viewingScoreId');
                  }}
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  New Calculation
                </button>
                <button 
                  className="px-6 py-3 bg-indigo-500/30 text-white rounded-lg font-medium hover:bg-inherit/40 transition-colors border border-indigo-300 flex items-center"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <Settings className="mr-2 h-5 w-5" />
                  Adjust Settings
                </button> */}
              </div>
            </div>
            
            {/* <div className="md:w-1/3 mt-8 md:mt-0">
              <div className="bg-white/10 rounded-xl p-4 border border-indigo-300/30">
                <div className="flex items-center mb-3">
                  <Info className="h-5 w-5 text-indigo-100 mr-2" />
                  <h3 className="text-white font-medium">Score Info</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-indigo-100 text-sm">Average Score</p>
                    <p className="text-white font-bold text-2xl">151</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Top 10% Score</p>
                    <p className="text-white font-bold text-2xl">165+</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Total Questions</p>
                    <p className="text-white font-bold text-2xl">101</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Sections</p>
                    <p className="text-white font-bold text-2xl">4</p>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Child routes will be rendered here */}
      <Outlet />
    </div>
  );
};

// Main Converter Page
export const ConverterMainPage: React.FC = () => {
  const navigate = useNavigate();
  
  // States for calculator
  const [selectedCurve, setSelectedCurve] = useState<string>('default');
  const [showCurveDropdown, setShowCurveDropdown] = useState<boolean>(false);
  const [totalCorrect, setTotalCorrect] = useState<number>(0);
  const [scaledScore, setScaledScore] = useState<number>(120);
  const [percentileRank, setPercentileRank] = useState<number>(0);
  const [viewingScoreId, setViewingScoreId] = useState<string | null>(null);
  const [sections, setSections] = useState<SectionConfig[]>(() => {
    // Try to load saved sections from localStorage
    const savedSections = localStorage.getItem('lsatSections');
    if (savedSections) {
      return JSON.parse(savedSections);
    }
    // Default sections if none saved
    return [
      { id: 'lr1', name: 'Logical Reasoning 1', totalQuestions: 25, correctAnswers: 0 },
      { id: 'lr2', name: 'Logical Reasoning 2', totalQuestions: 26, correctAnswers: 0 },
      { id: 'rc', name: 'Reading Comprehension', totalQuestions: 27, correctAnswers: 0 },
      { id: 'lg', name: 'Logic Games', totalQuestions: 23, correctAnswers: 0 }
    ];
  });
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [historyItems, setHistoryItems] = useState<any[]>(() => {
    // Load history from localStorage if available
    const savedHistory = localStorage.getItem('lsatScoreHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });
  
  // Calculate total questions
  const totalQuestions = sections.reduce((sum, section) => sum + section.totalQuestions, 0);
  
  // Load saved score if viewing from history
  useEffect(() => {
    const viewingScoreId = localStorage.getItem('viewingScoreId');
    if (viewingScoreId) {
      const savedHistory = localStorage.getItem('lsatScoreHistory');
      if (savedHistory) {
        const history = JSON.parse(savedHistory);
        const savedScore = history.find((item: any) => item.id.toString() === viewingScoreId);
        if (savedScore) {
          // Load the saved sections with their correct answers
          setSections(savedScore.sections);
          setTotalCorrect(savedScore.totalCorrect);
          setScaledScore(savedScore.scaledScore);
          setPercentileRank(savedScore.percentileRank);
          
          // Clear the viewing state to prevent reloading on subsequent renders
          localStorage.removeItem('viewingScoreId');
        }
      }
    }
  }, []);
  
  // Update the scaled score when total correct changes or curve changes
  useEffect(() => {
    // Calculate the total correct answers
    const newTotalCorrect = sections.reduce((sum, section) => sum + section.correctAnswers, 0);
    setTotalCorrect(newTotalCorrect);
    
    // Get the scaled score from the selected curve
    const rawScorePercentage = (newTotalCorrect / totalQuestions) * 101;
    const rawScoreRounded = Math.round(rawScorePercentage);
    
    // Use the selected scale or default to the default scale
    const selectedScale = curveScales[selectedCurve] || defaultLSATScale;
    const newScaledScore = selectedScale[rawScoreRounded.toString()] || 120;
    
    setScaledScore(newScaledScore);
    
    // Get the percentile rank
    setPercentileRank(percentiles[newScaledScore.toString()] || 0);
  }, [sections, totalQuestions, selectedCurve]);
  
  // Handle section score update
  const handleSectionScoreChange = (sectionId: string, correctAnswers: number) => {
    setSections(prevSections => 
      prevSections.map(section => 
        section.id === sectionId 
          ? { ...section, correctAnswers } 
          : section
      )
    );
  };
  
  // Save the current score to history
  const saveToHistory = () => {
    const newHistoryItem = {
      id: Date.now(),
      date: new Date().toLocaleDateString(),
      scaledScore,
      percentileRank,
      totalCorrect,
      totalQuestions,
      sections: [...sections]
    };
    
    const updatedHistory = [...historyItems, newHistoryItem];
    setHistoryItems(updatedHistory);
    
    // Save to localStorage
    localStorage.setItem('lsatScoreHistory', JSON.stringify(updatedHistory));
    
    // Show confirmation toast or notification
    alert('Score saved to history!');
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left Column - Section Scores */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Section Scores</h2>
            <p className="text-gray-600 mb-6">Enter the number of questions you answered correctly in each section.</p>
            
            <div className="space-y-6">
              {sections.map(section => (
                <div key={section.id} className="border-b border-gray-200 pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor={section.id} className="text-lg font-medium text-gray-700">
                      {section.name}
                    </label>
                    <span className="text-sm text-gray-500">
                      {section.correctAnswers} / {section.totalQuestions}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="range"
                      id={section.id}
                      min="0"
                      max={section.totalQuestions}
                      value={section.correctAnswers}
                      onChange={(e) => handleSectionScoreChange(section.id, parseInt(e.target.value))}
                      className="w-full h-2 bg-indigo-100 rounded-lg appearance-none cursor-pointer"
                    />
                    <input
                      type="number"
                      min="0"
                      max={section.totalQuestions}
                      value={section.correctAnswers}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (isNaN(val)) {
                          handleSectionScoreChange(section.id, 0);
                        } else if (val > section.totalQuestions) {
                          handleSectionScoreChange(section.id, section.totalQuestions);
                        } else {
                          handleSectionScoreChange(section.id, val);
                        }
                      }}
                      className="ml-4 w-16 p-1 text-center border border-gray-300 rounded"
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 flex items-center justify-between">
              <div>
                <span className="text-lg font-medium text-gray-700">Total Correct:</span>
                <span className="ml-2 text-xl font-bold text-indigo-600">{totalCorrect} / {totalQuestions}</span>
              </div>
              <div className="relative">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 flex items-center"
                  onClick={() => setShowCurveDropdown(!showCurveDropdown)}
                >
                  <span>{predefinedCurves.find(c => c.id === selectedCurve)?.name || 'Default Scale'}</span>
                  <ChevronDown className="ml-2 h-4 w-4" />
                </button>
                
                {showCurveDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10">
                    <div className="py-1">
                      {predefinedCurves.map(curve => (
                        <button
                          key={curve.id}
                          className={`block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100 w-full text-left ${selectedCurve === curve.id ? 'bg-indigo-50' : ''}`}
                          onClick={() => {
                            setSelectedCurve(curve.id);
                            setShowCurveDropdown(false);
                          }}
                        >
                          {curve.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Score Breakdown</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {sections.map(section => (
                <div key={section.id} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-800">{section.name}</h3>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600">Correct:</span>
                    <span className="font-bold">{section.correctAnswers} / {section.totalQuestions}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-600">Accuracy:</span>
                    <span className="font-bold">
                      {section.totalQuestions > 0 
                        ? `${Math.round((section.correctAnswers / section.totalQuestions) * 100)}%` 
                        : '0%'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Current Curve: </span>
                {predefinedCurves.find(c => c.id === selectedCurve)?.name || 'Default Scale'}
              </div>
              <div className="text-sm text-gray-600">
                A higher curve means the same raw score will result in a higher scaled score.
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                className="mr-4 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 flex items-center"
                onClick={() => navigate('history')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View History
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                onClick={saveToHistory}
              >
                <Save className="mr-2 h-4 w-4" />
                Save Result
              </button>
            </div>
          </div>
        </div>
        
        {/* Right Column - Results */}
        <div>
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Your Score</h2>
            
            <div className="bg-indigo-50 rounded-xl p-6 mb-6 flex flex-col items-center">
              <div className="text-6xl font-bold text-indigo-700 mb-2">{scaledScore}</div>
              <div className="text-gray-600">Scaled Score (120-180)</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Percentile:</span>
                <span className="font-bold">{percentileRank.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-gray-700">Raw Score:</span>
                <span className="font-bold">{totalCorrect} / {totalQuestions}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-gray-700">Overall Accuracy:</span>
                <span className="font-bold">
                  {totalQuestions > 0 
                    ? `${Math.round((totalCorrect / totalQuestions) * 100)}%` 
                    : '0%'}
                </span>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-gray-800">Score Interpretation</h3>
                <AlertCircle className="h-5 w-5 text-gray-400" />
              </div>
              
              <div className="text-gray-600 text-sm space-y-3">
                <p>
                  {scaledScore >= 170 ? 'Outstanding score! Top law schools territory.' : 
                   scaledScore >= 165 ? 'Excellent score! Competitive for T14 schools.' :
                   scaledScore >= 160 ? 'Strong score! Competitive for top 30 schools.' :
                   scaledScore >= 155 ? 'Good score! Competitive for top 50 schools.' :
                   scaledScore >= 150 ? 'Average score. Consider retaking for more options.' :
                   'Below average score. Consider additional preparation and retaking.'}
                </p>
                <p>Your score is higher than {percentileRank.toFixed(1)}% of test takers.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Next Steps</h2>
            
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-indigo-600" />
                </div>
                <span className="text-gray-600">Review your section performance</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-indigo-600" />
                </div>
                <span className="text-gray-600">Track your progress over time</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-indigo-600" />
                </div>
                <span className="text-gray-600">Create a study plan for weak areas</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-indigo-600" />
                </div>
                <span className="text-gray-600">Compare your score to target schools</span>
              </li>
            </ul>
            
            <button 
              className="mt-6 w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center"
              onClick={() => setShowSettingsModal(true)}
            >
              <Settings className="mr-2 h-4 w-4" />
              Customize Calculator
            </button>
          </div>
        </div>
      </div>
      
      {/* Settings Modal */}
      <SettingsModal 
        showSettingsModal={showSettingsModal}
        setShowSettingsModal={setShowSettingsModal}
      />
    </div>
  );
};

// Score History Page
export const ScoreHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Load history from localStorage
  const [history, setHistory] = useState<any[]>(() => {
    const savedHistory = localStorage.getItem('lsatScoreHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });
  
  // Delete history item
  const deleteHistoryItem = (id: number) => {
    const updatedHistory = history.filter(item => item.id !== id);
    setHistory(updatedHistory);
    localStorage.setItem('lsatScoreHistory', JSON.stringify(updatedHistory));
  };
  
  // View saved score
  const viewSavedScore = (item: any) => {
    // Save the selected score to localStorage so the main page can load it
    localStorage.setItem('viewingScoreId', item.id.toString());
    navigate('../');
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Score History</h2>
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => navigate('../')}
          >
            Back to Calculator
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scaled Score
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentile
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Raw Score
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {history.length > 0 ? (
                history.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-medium text-indigo-600">{item.scaledScore}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.percentileRank.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.totalCorrect} / {item.totalQuestions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                        onClick={() => viewSavedScore(item)}
                      >
                        View
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => deleteHistoryItem(item.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    No history records found. Save some scores to see them here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* <div className="mt-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Progress Chart</h3>
          <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">Chart will be displayed here</p>
          </div>
        </div> */}
      </div>
    </div>
  );
};

// Settings Page
export const ConverterSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State for calculator settings
  const [sections, setSections] = useState<SectionConfig[]>([
    { id: 'lr1', name: 'Logical Reasoning 1', totalQuestions: 25, correctAnswers: 0 },
    { id: 'lr2', name: 'Logical Reasoning 2', totalQuestions: 26, correctAnswers: 0 },
    { id: 'rc', name: 'Reading Comprehension', totalQuestions: 27, correctAnswers: 0 },
    { id: 'lg', name: 'Logic Games', totalQuestions: 23, correctAnswers: 0 }
  ]);
  
  // Handle section update
  const handleSectionUpdate = (sectionId: string, field: keyof SectionConfig, value: any) => {
    setSections(prevSections => 
      prevSections.map(section => 
        section.id === sectionId 
          ? { ...section, [field]: value } 
          : section
      )
    );
  };
  
  // Add new section
  const addNewSection = () => {
    const newId = `section-${Date.now()}`;
    setSections(prev => [...prev, { id: newId, name: 'New Section', totalQuestions: 25, correctAnswers: 0 }]);
  };
  
  // Remove section
  const removeSection = (sectionId: string) => {
    setSections(prev => prev.filter(section => section.id !== sectionId));
  };
  
  // Save settings
  const saveSettings = () => {
    // In a real app, this would save to localStorage or a database
    alert('Settings saved!');
    navigate('../');
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Calculator Settings</h2>
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => navigate('../')}
          >
            Back to Calculator
          </button>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Configure Sections</h3>
          <p className="text-gray-600 mb-4">
            Add, remove, or modify the sections to match your specific LSAT format.
          </p>
          
          <div className="space-y-4">
            {sections.map(section => (
              <div key={section.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <input
                    type="text"
                    value={section.name}
                    onChange={(e) => handleSectionUpdate(section.id, 'name', e.target.value)}
                    className="text-lg font-medium text-gray-700 border-b border-gray-200 focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    className="text-red-600 hover:text-red-900"
                    onClick={() => removeSection(section.id)}
                  >
                    Remove
                  </button>
                </div>
                
                <div className="flex items-center mb-2">
                  <label className="text-gray-600 w-40">Total Questions:</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={section.totalQuestions}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val) && val > 0) {
                        handleSectionUpdate(section.id, 'totalQuestions', val);
                      }
                    }}
                    className="ml-4 w-24 p-1 text-center border border-gray-300 rounded"
                  />
                </div>
              </div>
            ))}
          </div>
          
          <button
            className="mt-4 px-4 py-2 border border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50"
            onClick={addNewSection}
          >
            + Add New Section
          </button>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Customize Score Scale</h3>
          <p className="text-gray-600 mb-4">
            Adjust the conversion from raw score to scaled score (120-180).
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-600 mb-2">
              Currently using: <span className="font-medium">Default Scale</span>
            </p>
            <button className="text-indigo-600 hover:text-indigo-900">
              Edit Custom Scale
            </button>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            onClick={saveSettings}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom settings modal
export const SettingsModal: React.FC<{
  showSettingsModal: boolean;
  setShowSettingsModal: (show: boolean) => void;
}> = ({ showSettingsModal, setShowSettingsModal }) => {
  if (!showSettingsModal) return null;
  
  // State for modal settings
  const [localSections, setLocalSections] = useState<SectionConfig[]>([
    { id: 'lr1', name: 'Logical Reasoning 1', totalQuestions: 25, correctAnswers: 0 },
    { id: 'lr2', name: 'Logical Reasoning 2', totalQuestions: 26, correctAnswers: 0 },
    { id: 'rc', name: 'Reading Comprehension', totalQuestions: 27, correctAnswers: 0 },
    { id: 'lg', name: 'Logic Games', totalQuestions: 23, correctAnswers: 0 }
  ]);
  
  // Load current settings when modal opens
  useEffect(() => {
    const savedSections = localStorage.getItem('lsatSections');
    if (savedSections) {
      setLocalSections(JSON.parse(savedSections));
    }
  }, []);
  
  // Handle section update
  const handleSectionUpdate = (sectionId: string, field: keyof SectionConfig, value: any) => {
    setLocalSections(prevSections => 
      prevSections.map(section => 
        section.id === sectionId 
          ? { ...section, [field]: value } 
          : section
      )
    );
  };
  
  // Add new section
  const addNewSection = () => {
    const newId = `section-${Date.now()}`;
    setLocalSections(prev => [...prev, { id: newId, name: 'New Section', totalQuestions: 25, correctAnswers: 0 }]);
  };
  
  // Remove section
  const removeSection = (sectionId: string) => {
    setLocalSections(prev => prev.filter(section => section.id !== sectionId));
  };
  
  // Save settings
  const saveSettings = () => {
    localStorage.setItem('lsatSections', JSON.stringify(localSections));
    setShowSettingsModal(false);
    
    // Force page reload to apply changes
    window.location.reload();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Customize Calculator</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={() => setShowSettingsModal(false)}
          >
            &times;
          </button>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Customize Sections</h3>
          <p className="text-gray-600 mb-4">
            Add, remove, or modify the sections to match your specific LSAT format.
          </p>
          
          <div className="space-y-4">
            {localSections.map(section => (
              <div key={section.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <input
                    type="text"
                    value={section.name}
                    onChange={(e) => handleSectionUpdate(section.id, 'name', e.target.value)}
                    className="text-lg font-medium text-gray-700 border-b border-gray-200 focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    className="text-red-600 hover:text-red-900"
                    onClick={() => removeSection(section.id)}
                    disabled={localSections.length <= 1}
                  >
                    Remove
                  </button>
                </div>
                
                <div className="flex items-center mb-2">
                  <label className="text-gray-600 w-40">Total Questions:</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={section.totalQuestions}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val) && val > 0) {
                        handleSectionUpdate(section.id, 'totalQuestions', val);
                      }
                    }}
                    className="ml-4 w-24 p-1 text-center border border-gray-300 rounded"
                  />
                </div>
              </div>
            ))}
          </div>
          
          <button
            className="mt-4 px-4 py-2 border border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50"
            onClick={addNewSection}
          >
            + Add New Section
          </button>
        </div>
        
        <div className="flex justify-end mt-8">
          <button
            className="mr-4 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => setShowSettingsModal(false)}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            onClick={saveSettings}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
